import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck, Coffee } from "lucide-react";
import LiveTicker from "./LiveTicker";
import VisitorCounter from "./VisitorCounter";
import SubscribeModal from "./SubscribeModal";
import SearchDropdown from "./SearchDropdown";
import MobileSearchTrigger from "./MobileSearchTrigger";
import { useAuth } from "@/lib/auth";

const WORDMARK_URL = "/logo-wordmark.png";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/news", label: "News" },
  { to: "/ipos", label: "IPOs" },
  { to: "/drhp", label: "DRHP" },
  { to: "/commodities", label: "Commodities" },
  { to: "/crypto", label: "Crypto" },
  { to: "/real-estate", label: "Real Estate" },
  { to: "/watchlist", label: "Watchlist" },
  { to: "/blogs", label: "Blogs" },
];

export default function Header() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [subOpen, setSubOpen] = useState(false);
  const scrollTop = () => window.scrollTo({ top: 0, left: 0, behavior: "smooth" });

  return (
    <header className="sticky top-0 z-40 backdrop-blur border-b border-[#E2DDD3]" style={{ background: "rgba(247,244,238,0.95)" }}>
      <LiveTicker />

      {/* Masthead row */}
      <div className="px-4 sm:px-6 lg:px-10 pt-3 pb-2 flex items-start justify-between gap-4">
        <Link to="/" onClick={scrollTop} className="flex items-center shrink-0" data-testid="brand-logo">
          <img
            src={WORDMARK_URL}
            alt="Nivesh Street"
            className="h-5 sm:h-[22px] md:h-6 w-auto object-contain mix-blend-multiply"
          />
          <span className="hidden md:inline-block ml-4 pl-4 border-l border-[#E2DDD3] text-[11px] text-[#6B6C6F] max-w-[260px] leading-snug">
            Markets. Ideas. Research.<br/>For a more informed Investor.
          </span>
        </Link>

        <div className="hidden md:flex items-center flex-1 max-w-md mx-6">
          <SearchDropdown />
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <MobileSearchTrigger />
          <VisitorCounter />
          <Link
            to="/support"
            onClick={scrollTop}
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FFDD00] text-[#0A0A0A] text-xs font-bold hover:brightness-95 shadow-sm"
            data-testid="header-bmc"
            title="Buy us a coffee"
          >
            <Coffee className="w-3.5 h-3.5" /> Coffee
          </Link>
          <Link
            to="/support"
            onClick={scrollTop}
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full bg-[#FFDD00] text-[#0A0A0A] shadow-sm"
            data-testid="header-bmc-mobile"
            aria-label="Buy us a coffee"
          >
            <Coffee className="w-4 h-4" />
          </Link>
          {user ? (
            <>
              <button
                onClick={() => nav("/admin")}
                className="hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D91E2A]/10 text-[#D91E2A] border border-[#D91E2A]/30 text-xs font-semibold hover:bg-[#D91E2A]/20"
                data-testid="header-admin-btn"
              >
                <ShieldCheck className="w-3.5 h-3.5" /> Admin
              </button>
              <button onClick={() => { logout(); nav("/"); }} className="p-2 rounded-md hover:bg-black/5" data-testid="header-logout-btn" aria-label="Logout">
                <LogOut className="w-4 h-4 text-[#3A3B3E]" />
              </button>
            </>
          ) : (
            <>
              <button
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#D91E2A] text-white text-sm font-semibold hover:bg-[#B8141F] shadow-sm"
                data-testid="header-subscribe"
                onClick={() => setSubOpen(true)}
              >
                Subscribe
              </button>
            </>
          )}
        </div>
      </div>

      {/* Primary nav — center aligned */}
      <nav className="px-4 sm:px-6 lg:px-10 pb-2 flex overflow-x-auto no-scrollbar items-center justify-start md:justify-center gap-1 border-t border-[#E2DDD3]/70 pt-1.5">
        {navItems.map(n => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            onClick={scrollTop}
            data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            className={({ isActive }) =>
              `whitespace-nowrap px-3 py-1.5 rounded text-[13px] font-semibold transition-colors ${
                isActive
                  ? "text-[#D91E2A] bg-[#D91E2A]/8"
                  : "text-[#3A3B3E] hover:text-[#1A1A1A] hover:bg-black/[0.04]"
              }`
            }
          >
            {n.label}
          </NavLink>
        ))}
      </nav>
      <SubscribeModal open={subOpen} onOpenChange={setSubOpen} />
    </header>
  );
}
